import type { ReactNode } from "react"

interface CalendarWindowProps {
  windowStart: Date
  windowEnd: Date
  today?: Date
  /** When true, render a single month (window start). Used at <md breakpoints. */
  compact?: boolean
  /** Pulled out as the right-hand stat alongside the window stats. */
  rightStat?: ReactNode
  windowLabel?: string
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const WEEK_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

function startWeekdayMonZero(year: number, monthIndex: number) {
  // JS Date: 0=Sun..6=Sat. Convert to Mon=0..Sun=6.
  const jsDay = new Date(year, monthIndex, 1).getDay()
  return (jsDay + 6) % 7
}

function daysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate()
}

function formatMonthDay(d: Date) {
  return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`
}

export function CalendarWindow({
  windowStart,
  windowEnd,
  today = new Date(),
  compact = false,
  rightStat,
  windowLabel = "Pre-emergent window",
}: CalendarWindowProps) {
  const startMonthIndex = windowStart.getMonth()
  const startYear = windowStart.getFullYear()
  const nextMonthIndex = (startMonthIndex + 1) % 12
  const nextMonthYear = startMonthIndex === 11 ? startYear + 1 : startYear
  const monthsToRender = compact
    ? [{ year: startYear, month: startMonthIndex }]
    : [
        { year: startYear, month: startMonthIndex },
        { year: nextMonthYear, month: nextMonthIndex },
      ]

  const durationDays = Math.max(
    1,
    Math.round((windowEnd.getTime() - windowStart.getTime()) / 86400000) + 1,
  )
  const daysToOpen = Math.round((windowStart.getTime() - today.getTime()) / 86400000)
  const daysToClose = Math.round((windowEnd.getTime() - today.getTime()) / 86400000)

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-xs sm:p-6 md:p-7">
      <div className="flex flex-wrap items-start gap-x-5 gap-y-3 sm:gap-x-8 sm:gap-y-4">
        <Stat
          eyebrow="Window opens"
          big={formatMonthDay(windowStart)}
          sub={
            daysToOpen > 0
              ? `${daysToOpen} ${daysToOpen === 1 ? "day" : "days"} from today`
              : "Open today"
          }
          tone="green"
        />
        <Stat
          eyebrow="Duration"
          big={`${durationDays} days`}
          sub={durationDays > 14 ? "Plenty of time" : "Tight window"}
          tone="ink"
        />
        <Stat
          eyebrow="Window closes"
          big={formatMonthDay(windowEnd)}
          sub={
            daysToClose > 0
              ? `${daysToClose} ${daysToClose === 1 ? "day" : "days"} from today`
              : "Closed"
          }
          tone="amber"
        />
        {rightStat && <div className="w-full sm:ml-auto sm:w-auto">{rightStat}</div>}
      </div>

      <div className="mt-6 space-y-5 sm:mt-7 sm:space-y-6">
        {monthsToRender.map(({ year, month }) => (
          <CalendarMonth
            key={`${year}-${month}`}
            year={year}
            month={month}
            windowStart={windowStart}
            windowEnd={windowEnd}
            today={today}
            windowLabel={windowLabel}
          />
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-4 border-t border-gray-100 pt-4 text-xs text-gray-500 sm:mt-6 sm:gap-5">
        <Legend
          swatch={<span className="block h-3.5 w-3.5 rounded-sm border-2 border-green-700 bg-green-600" />}
          label="Today"
        />
        <Legend
          swatch={<span className="block h-3.5 w-3.5 rounded-sm border border-green-600 bg-green-100" />}
          label={windowLabel}
        />
      </div>
    </div>
  )
}

interface CalendarMonthProps {
  year: number
  month: number
  windowStart: Date
  windowEnd: Date
  today: Date
  windowLabel: string
}

function CalendarMonth({ year, month, windowStart, windowEnd, today, windowLabel }: CalendarMonthProps) {
  const days = daysInMonth(year, month)
  const offset = startWeekdayMonZero(year, month)
  const cells: ({ blank: true } | { d: number })[] = []
  for (let i = 0; i < offset; i++) cells.push({ blank: true })
  for (let d = 1; d <= days; d++) cells.push({ d })
  while (cells.length % 7 !== 0) cells.push({ blank: true })

  const monthHasOpen =
    windowStart.getFullYear() === year && windowStart.getMonth() === month
  const monthHasClose =
    windowEnd.getFullYear() === year && windowEnd.getMonth() === month

  return (
    <div>
      {/* Month + window label: stacked on mobile so the long "Pre-emergent
          window: Mar 15 to Apr 15" string doesn't crush the month title. */}
      <div className="mb-2.5 flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
        <div className="text-lg font-bold tracking-tight text-gray-900">
          {MONTH_NAMES[month]} {year}
        </div>
        {(monthHasOpen || monthHasClose) && (
          <div className="text-xs font-semibold text-green-700">
            {windowLabel}: {formatMonthDay(windowStart)} to {formatMonthDay(windowEnd)}
          </div>
        )}
      </div>
      <div className="mb-1.5 grid grid-cols-7 gap-1 sm:gap-1.5">
        {WEEK_HEADERS.map((h) => (
          <div
            key={h}
            className="text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400"
          >
            {h}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((c, i) => {
          if ("blank" in c) return <div key={`b-${i}`} />
          const cellDate = new Date(year, month, c.d)
          const inWindow = cellDate >= startOfDay(windowStart) && cellDate <= startOfDay(windowEnd)
          const isOpenDay = sameDay(cellDate, windowStart)
          const isCloseDay = sameDay(cellDate, windowEnd)
          const isToday = sameDay(cellDate, today)

          // Square-ish cells on mobile read better than the desktop 1.4:1
          // (which compresses height to ~28px when the grid is narrow).
          const base =
            "min-h-[44px] aspect-square sm:aspect-[1.4/1] rounded-md sm:rounded-lg p-1 sm:p-1.5 flex flex-col justify-between text-xs"
          let visual: string
          if (isToday) {
            visual = "border-2 border-green-700 bg-green-600 text-white font-bold"
          } else if (inWindow) {
            visual = "border border-green-600 bg-green-100 text-green-800 font-semibold"
          } else {
            visual = "border border-gray-100 bg-white text-gray-900"
          }
          const classes = `${base} ${visual}`

          return (
            <div key={`c-${c.d}`} className={classes}>
              <span className="tabular-nums">{c.d}</span>
              {isToday && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-white">Today</span>
              )}
              {!isToday && isOpenDay && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-green-700">Opens</span>
              )}
              {!isToday && isCloseDay && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-green-700">Closes</span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface StatProps {
  eyebrow: string
  big: ReactNode
  sub: string
  tone: "green" | "amber" | "ink"
}

export function Stat({ eyebrow, big, sub, tone }: StatProps) {
  const eyebrowColor =
    tone === "green" ? "text-green-700" : tone === "amber" ? "text-amber-600" : "text-gray-900"
  return (
    <div>
      <div className={`text-[11px] font-bold uppercase tracking-wider ${eyebrowColor}`}>
        {eyebrow}
      </div>
      <div className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900 tabular-nums">
        {big}
      </div>
      <div className="mt-0.5 text-xs text-gray-500">{sub}</div>
    </div>
  )
}

function Legend({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      {swatch}
      {label}
    </span>
  )
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
